import { type DivIcon, LatLng, type Map as LeafletMap } from 'leaflet';
import { NoddiButton, NoddiDialog } from 'noddi-ui-web';
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { Marker } from 'react-leaflet';
import { LocationPicker } from './LocationPicker';
import { MapInstanceCapture } from './MapInstanceCapture';
import { MapPinRestriction } from './MapPinRestriction';
import { MapPinSelectionLegend } from './MapPinSelectionLegend';
import { getNoddiLeafletIcon } from './markers/getNoddiLeafletIcon';
import {
  clampLatLngToRadius,
  getInitialRestrictedCoords,
  MAP_PIN_RESTRICTION_RADIUS_M
} from './pinLocationRestriction';

type Props = {
  initialLatitude?: number;
  initialLongitude?: number;
  addressLatitude?: number;
  addressLongitude?: number;
  /**
   * Distance limit around the address. Defaults to booking radius.
   * Pass `null` to show the address marker without a circle / clamp (e.g. building ports).
   */
  restrictionRadiusMeters?: number | null;
  onLocationSelected: (latitude: number, longitude: number) => void | Promise<void>;
  isOpen?: boolean;
  onClose?: () => void;
  isLoading?: boolean;
  title?: string;
  pickupLabel?: string;
  addressLabel?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  centerPickupTitle?: string;
  centerAddressTitle?: string;
  allowedAreaLabel?: string;
  /** Override selected-location map marker (defaults to blue pin). */
  pickupMarkerIcon?: DivIcon;
  /** Override address/restriction-center map marker (defaults to red pin). */
  addressMarkerIcon?: DivIcon;
  /** Override selected-location legend glyph. */
  pickupLegendIcon?: ReactNode;
  /** Override address legend glyph. */
  addressLegendIcon?: ReactNode;
};

export const MapPinSelectionDialog = ({
  initialLatitude,
  initialLongitude,
  addressLatitude,
  addressLongitude,
  restrictionRadiusMeters = MAP_PIN_RESTRICTION_RADIUS_M,
  onLocationSelected,
  isOpen = false,
  onClose,
  isLoading = false,
  title = 'Select exact location',
  pickupLabel = 'Car location',
  addressLabel = 'Address',
  confirmLabel = 'Confirm location',
  cancelLabel = 'Cancel',
  centerPickupTitle = 'Center map on pin',
  centerAddressTitle = 'Center map on address',
  allowedAreaLabel,
  pickupMarkerIcon,
  addressMarkerIcon,
  pickupLegendIcon,
  addressLegendIcon
}: Props) => {
  // Mounted only while open so the picker starts from the current props every time.
  if (!isOpen) {
    return null;
  }

  return (
    <MapPinSelectionContent
      initialLatitude={initialLatitude}
      initialLongitude={initialLongitude}
      addressLatitude={addressLatitude}
      addressLongitude={addressLongitude}
      restrictionRadiusMeters={restrictionRadiusMeters}
      onLocationSelected={onLocationSelected}
      onClose={onClose}
      isLoading={isLoading}
      title={title}
      pickupLabel={pickupLabel}
      addressLabel={addressLabel}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      centerPickupTitle={centerPickupTitle}
      centerAddressTitle={centerAddressTitle}
      allowedAreaLabel={allowedAreaLabel}
      pickupMarkerIcon={pickupMarkerIcon}
      addressMarkerIcon={addressMarkerIcon}
      pickupLegendIcon={pickupLegendIcon}
      addressLegendIcon={addressLegendIcon}
    />
  );
};

type ContentProps = {
  initialLatitude?: number;
  initialLongitude?: number;
  addressLatitude?: number;
  addressLongitude?: number;
  restrictionRadiusMeters: number | null;
  onLocationSelected: (latitude: number, longitude: number) => void | Promise<void>;
  onClose?: () => void;
  isLoading: boolean;
  title: string;
  pickupLabel: string;
  addressLabel: string;
  confirmLabel: string;
  cancelLabel: string;
  centerPickupTitle: string;
  centerAddressTitle: string;
  allowedAreaLabel?: string;
  pickupMarkerIcon?: DivIcon;
  addressMarkerIcon?: DivIcon;
  pickupLegendIcon?: ReactNode;
  addressLegendIcon?: ReactNode;
};

const MapPinSelectionContent = ({
  initialLatitude,
  initialLongitude,
  addressLatitude,
  addressLongitude,
  restrictionRadiusMeters,
  onLocationSelected,
  onClose,
  isLoading,
  title,
  pickupLabel,
  addressLabel,
  confirmLabel,
  cancelLabel,
  centerPickupTitle,
  centerAddressTitle,
  allowedAreaLabel,
  pickupMarkerIcon,
  addressMarkerIcon,
  pickupLegendIcon,
  addressLegendIcon
}: ContentProps) => {
  const hasAddress = addressLatitude !== undefined && addressLongitude !== undefined;
  const hasRestriction = hasAddress && restrictionRadiusMeters !== null && restrictionRadiusMeters > 0;

  const [selectedCoords, setSelectedCoords] = useState<{ lat: number; lng: number } | null>(() => {
    if (!hasRestriction) {
      if (initialLatitude === undefined || initialLongitude === undefined) {
        return null;
      }
      return { lat: initialLatitude, lng: initialLongitude };
    }

    return getInitialRestrictedCoords({
      initialLatitude,
      initialLongitude,
      addressLatitude,
      addressLongitude,
      radiusMeters: restrictionRadiusMeters
    });
  });

  const mapRef = useRef<LeafletMap | null>(null);

  const handleConfirm = async () => {
    if (!selectedCoords) {
      return;
    }
    await onLocationSelected(selectedCoords.lat, selectedCoords.lng);
    onClose?.();
  };

  const centerOn = (lat: number, lng: number) => {
    const map = mapRef.current;
    if (!map) {
      return;
    }
    map.flyTo([lat, lng], map.getZoom(), { duration: 0.4 });
  };

  return (
    <NoddiDialog
      open
      onClose={() => onClose?.()}
      title={title}
      // Actions live in the footer, outside the scroll region — the map is tall enough on small
      // screens to push them out of view, and dragging it pans the map instead of scrolling.
      footer={
        <div className='flex justify-end gap-3'>
          <NoddiButton variant='outline' onClick={onClose}>
            {cancelLabel}
          </NoddiButton>
          {/* oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs handleConfirm(); React discards the returned promise, so a rejection is unhandled unless the body catches */}
          <NoddiButton onClick={handleConfirm} disabled={!selectedCoords} loading={isLoading}>
            {confirmLabel}
          </NoddiButton>
        </div>
      }
    >
      <div className='relative'>
        <LocationPicker
          latitude={selectedCoords?.lat ?? initialLatitude}
          longitude={selectedCoords?.lng ?? initialLongitude}
          defaultLayer='satellite'
          markerIcon={pickupMarkerIcon}
          onLocationChange={(lat, lng) => {
            if (!hasRestriction) {
              setSelectedCoords({ lat, lng });
              return;
            }

            const clamped = clampLatLngToRadius(
              new LatLng(lat, lng),
              new LatLng(addressLatitude, addressLongitude),
              restrictionRadiusMeters
            );
            setSelectedCoords({ lat: clamped.lat, lng: clamped.lng });
          }}
        >
          <MapInstanceCapture mapRef={mapRef} />
          {hasRestriction && (
            <MapPinRestriction
              centerLat={addressLatitude}
              centerLng={addressLongitude}
              radiusMeters={restrictionRadiusMeters}
              centerMarkerIcon={addressMarkerIcon}
            />
          )}
          {hasAddress && !hasRestriction && (
            <Marker
              position={[addressLatitude, addressLongitude]}
              icon={addressMarkerIcon ?? getNoddiLeafletIcon({ redMarker: true })}
              interactive={false}
            />
          )}
        </LocationPicker>
        <MapPinSelectionLegend
          pickupLabel={pickupLabel}
          addressLabel={hasAddress ? addressLabel : null}
          radiusMeters={hasRestriction ? restrictionRadiusMeters : undefined}
          onCenterPickup={selectedCoords ? () => centerOn(selectedCoords.lat, selectedCoords.lng) : undefined}
          onCenterAddress={hasAddress ? () => centerOn(addressLatitude, addressLongitude) : undefined}
          centerPickupTitle={centerPickupTitle}
          centerAddressTitle={centerAddressTitle}
          allowedAreaLabel={
            allowedAreaLabel ?? (hasRestriction ? `Allowed area (${restrictionRadiusMeters} m)` : undefined)
          }
          pickupIcon={pickupLegendIcon}
          addressIcon={addressLegendIcon}
        />
      </div>
    </NoddiDialog>
  );
};
